/**
 * Prints of a signed invoice (DGI §2.11, §2.18, §3.f), as `InvoiceVersion`
 * traces: the print of the original, at most one per invoice, and each later
 * reprint as a duplicata, ranked among its site's copies and printed with
 * the bare mention DUPLICATA; a controlled reissue of the original (rule
 * C, invoice module) and the release of the original, after which every
 * emission is a duplicata.
 */
export const INVOICE_PRINT_CHANGE_TYPES = {
    ORIGINAL: "ORIGINAL_PRINT",
    DUPLICATA: "DUPLICATA",
    ORIGINAL_REISSUE: "ORIGINAL_REISSUE",
    ORIGINAL_RELEASED: "ORIGINAL_RELEASED",
};
/**
 * The desktop's two-phase print (§6.6 of the ticket design): a claim
 * reserves the next emission for a silent print job, a renewal moves its
 * expiry while the print dialog is open, a cancellation frees it. They stay
 * in the database that took them and never sync: what syncs is the
 * `ORIGINAL_PRINT` or `DUPLICATA` that a confirm, or the claim's expiry,
 * records.
 */
export const INVOICE_PRINT_CLAIM_CHANGE_TYPES = {
    CLAIM: "PRINT_CLAIM",
    CANCELLED: "PRINT_CLAIM_CANCELLED",
    RENEWED: "PRINT_CLAIM_RENEWED",
};
/**
 * How long a claim holds the invoice: until then every other emission of it
 * is refused; past it, an unsettled claim counts as printed.
 */
export const PRINT_CLAIM_TTL_SECONDS = 60;
/**
 * Longest life of a claim from its taking. Its claimant renews a live claim
 * by `PRINT_CLAIM_TTL_SECONDS` at a time, cut at this; past it, the claim
 * expires and counts as printed.
 */
export const PRINT_CLAIM_MAX_LIFE_SECONDS = 600;
/**
 * How often the desktop renews its claim while the print job is pending;
 * below the TTL. A claim held to its longest life gets 12 renewals (at 45 s
 * … 540 s), so a print writes at most 12 `PRINT_CLAIM_RENEWED` rows.
 */
export const PRINT_CLAIM_RENEWAL_INTERVAL_SECONDS = 45;
/**
 * The least a renewal moves a claim's expiry to be written: half the
 * desktop's interval (22.5 s). A renewal that moves it less writes nothing,
 * unless it sets the claim's longest life, so a claim never lapses before
 * its deadline and holds at most 24 renewal rows whatever its client.
 */
export const PRINT_CLAIM_RENEWAL_MIN_STEP_SECONDS = PRINT_CLAIM_RENEWAL_INTERVAL_SECONDS / 2;
/** Longest failure reason a cancelled claim records. */
export const PRINT_CLAIM_FAILURE_REASON_MAX_LENGTH = 500;
/**
 * Cancelled claims of an invoice's original after which the original can
 * no longer be claimed. Each cancelled ORIGINAL claim handed out a ticket
 * without a DUPLICATA mention; past the cap the original is emitted only by
 * an emission that consumes it at once (`/ticket`, `/pdf`).
 */
export const PRINT_CLAIM_ORIGINAL_CANCELLATIONS_MAX = 3;
/**
 * Where a print was made, recorded in its trace: the cloud, or an on-site
 * install (node or desktop) printing from its own database.
 */
export const INVOICE_PRINT_SITES = {
    CLOUD: "CLOUD",
    ON_SITE: "ON_SITE",
};
/**
 * The document an emission hands out: the A4 invoice or the till ticket at
 * its paper width. One original/duplicata counter covers all three.
 */
export const INVOICE_PRINT_FORMATS = {
    A4: "A4",
    TICKET_58: "TICKET_58",
    TICKET_80: "TICKET_80",
};
/**
 * What triggered an emission: the till's automatic print after payment, a
 * user's click, or a download through the public payment link.
 */
export const INVOICE_PRINT_CHANNELS = {
    AUTO: "AUTO",
    MANUAL: "MANUAL",
    PUBLIC_LINK: "PUBLIC_LINK",
};
/**
 * Where the invoice was issued, frozen in the trace of its original: a till
 * sale (a POS transaction of the tenant points to the invoice) or the
 * invoice module. A later decision reads the frozen value, because POS
 * transactions do not sync between a node and the cloud.
 */
export const INVOICE_PRINT_CONTEXTS = {
    POS: "POS",
    INVOICE: "INVOICE",
};
/**
 * Why an original is reissued (rule C): a closed list, each with the label
 * the screens and the history print.
 */
export const ORIGINAL_REISSUE_REASONS = {
    IMPRESSION_DEFECTUEUSE: "Impression défectueuse",
    ORIGINAL_ABIME_OU_PERDU: "Original abîmé ou perdu avant envoi",
    ERREUR_SIGNATURE_CACHET: "Erreur de signature ou de cachet",
};
export const ORIGINAL_REISSUE_REASON_CODES = Object.keys(ORIGINAL_REISSUE_REASONS);
/** Longest comment a reissue carries. */
export const ORIGINAL_REISSUE_COMMENT_MAX_LENGTH = 500;
/** Bounds of the tenant's reissue cap per invoice (`Tenant.originalReissueMax`). */
export const ORIGINAL_REISSUE_MAX_BOUNDS = { min: 0, max: 5 };
/**
 * Bounds of the tenant's automatic release delay in days
 * (`Tenant.originalReleaseAfterDays`); null means never.
 */
export const ORIGINAL_RELEASE_AFTER_DAYS_BOUNDS = { min: 0, max: 365 };
/**
 * How an original was released, recorded in its `ORIGINAL_RELEASED` trace:
 * a user marked it handed over, or the platform handed the invoice out. The
 * release by delay is never written; it is derived from the original's date.
 */
export const ORIGINAL_RELEASE_MODES = {
    MANUAL: "MANUAL",
    SENT: "SENT",
};
/**
 * What handed the invoice out, on a release by sending: the invoice PDF
 * served by the public payment page.
 */
export const ORIGINAL_RELEASE_CHANNELS = {
    PUBLIC_LINK: "PUBLIC_LINK",
};
/**
 * The Tenant columns of rule C (#929): whether an original may be issued
 * again, how many times, and after how many days it counts as released.
 * Every channel that carries one of them carries all of them.
 */
export const ORIGINAL_REISSUE_TENANT_SETTINGS = [
    "originalReissueEnabled",
    "originalReissueMax",
    "originalReleaseAfterDays",
];
//# sourceMappingURL=invoice-print.js.map